import React from 'react';
import { Box, Button, IconButton, Pagination, TextField, Tooltip, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import styles from './ProfilesList.module.scss';

/** Same breakpoint as `breakpoints.$mobile` in scss/settings/_breakpoints.scss. */
const MOBILE_QUERY = '(max-width: 767px)';

export type ProfilesToolbarProps = {
  /** Prefix of the element ids (kept as before for click tracking), e.g. `customerProfiles`. */
  idPrefix: string;
  /** Accessible name of the card / list switcher group. */
  viewLabel: string;
  searchValue: string;
  setSearchValue: (value: string) => void;
  searchCleared: boolean;
  setSearchCleared: (value: boolean) => void;
  resetSearchFilter: () => void;
  showPagination: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  paginationDisabled?: boolean;
  cardView: boolean;
  setCardView: (value: boolean) => void;
  /** "Add profile" button shown first in the block, before the search. */
  addButton?: {
    id: string;
    label: string;
    onClick: () => void;
  };
};

/**
 * Actions block of the Customers / Suppliers pages, built like the Document Vault block header:
 * the "Add profile" button and the search on the left, then the pagination and the card / list view switcher on the right.
 */
export const ProfilesToolbar = ({
  idPrefix,
  viewLabel,
  searchValue,
  setSearchValue,
  searchCleared,
  setSearchCleared,
  resetSearchFilter,
  showPagination,
  currentPage,
  totalPages,
  onPageChange,
  paginationDisabled,
  cardView,
  setCardView,
  addButton,
}: ProfilesToolbarProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  return (
    <Box id={`${idPrefix}ActionsBlock`} className={styles.block_header}>
      {addButton && (
        <Button
          id={addButton.id}
          onClick={addButton.onClick}
          variant='contained'
          color='primary'
          className={styles.add_profile_button}
        >
          {addButton.label}
        </Button>
      )}
      <TextField
        id={`${idPrefix}SearchInput`}
        className={styles.search}
        placeholder={t('labels.searchByName')}
        slotProps={{
          htmlInput: { 'aria-label': t('labels.searchByName') },
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon
                  id={`${idPrefix}SearchInputSearchIcon`}
                  className={styles.search_icon}
                />
              </InputAdornment>
            ),
            endAdornment: searchValue.trim() ? (
              <InputAdornment position='end'>
                <IconButton
                  id={`${idPrefix}ResetSearchFilterIconBtn`}
                  size='small'
                  aria-label={t('buttons.close')}
                  onClick={() => resetSearchFilter()}
                >
                  <CloseIcon id={`${idPrefix}ResetSearchFilterIconBtnCloseIcon`} fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
          if (e.target.value.trim().length > 0) {
            setSearchCleared(false);
          }
        }}
        onKeyUp={(e) => {
          if ((e.target as HTMLInputElement).value.trim() === '' && !searchCleared) {
            resetSearchFilter();
          }
        }}
      />
      <Box className={styles.header_controls}>
        {showPagination && (
          <Box id={`${idPrefix}PaginationBox`} className={styles.header_pagination}>
            <Typography className={styles.pages_value} variant='caption'>
              {t('labels.pageOfTotal', { page: currentPage, total: totalPages })}
            </Typography>
            <Pagination
              id={`${idPrefix}PagePagination`}
              disabled={paginationDisabled}
              count={totalPages}
              page={currentPage}
              onChange={onPageChange}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className={styles.pagination}
            />
          </Box>
        )}
        <Box className={styles.card_list_view_container} role='group' aria-label={viewLabel}>
          <Tooltip title={t('buttons.card')} placement='top' arrow>
            <button
              id={`${idPrefix}CardBtn`}
              type='button'
              aria-pressed={cardView}
              className={`${styles.variant_btn}${cardView ? ` ${styles.variant_btn_active}` : ''}`}
              onClick={() => setCardView(true)}
            >
              <DashboardIcon id={`${idPrefix}CardBtnDashboardIcon`} />
              <span id={`${idPrefix}CardBtnText`} className={styles.visually_hidden}>
                {t('buttons.card')}
              </span>
            </button>
          </Tooltip>
          <Tooltip title={t('buttons.list')} placement='top' arrow>
            <button
              id={`${idPrefix}ListBtn`}
              type='button'
              aria-pressed={!cardView}
              className={`${styles.variant_btn}${!cardView ? ` ${styles.variant_btn_active}` : ''}`}
              onClick={() => setCardView(false)}
            >
              <BallotIcon id={`${idPrefix}ListBtnBallotIcon`} />
              <span id={`${idPrefix}ListBtnText`} className={styles.visually_hidden}>
                {t('buttons.list')}
              </span>
            </button>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfilesToolbar;
